import React, { useState } from "react";
import { Modal } from "../common/Modal";
import { Opportunity } from "../../types";
import { Trash2, Archive, AlertTriangle } from "lucide-react";

interface DeleteOpportunityModalProps {
  isOpen: boolean;
  onClose: () => void;
  opportunity: Opportunity | null;
  onConfirmDelete: (id: string) => Promise<void>;
  onArchiveInstead?: (opp: Opportunity) => void;
}

export const DeleteOpportunityModal: React.FC<DeleteOpportunityModalProps> = ({
  isOpen,
  onClose,
  opportunity,
  onConfirmDelete,
  onArchiveInstead,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!opportunity) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirmDelete(opportunity.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete opportunity.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="md"
      title="Delete Opportunity Record"
      description="Permanent removal confirmation."
    >
      <div className="space-y-4">
        {error && (
          <div className="rounded-lg bg-rose-50 p-3 text-xs font-semibold text-rose-700 dark:bg-rose-950/50 dark:text-rose-300">
            {error}
          </div>
        )}

        <div className="rounded-xl border border-rose-200 bg-rose-50/70 p-3.5 text-xs text-rose-900 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200 flex items-start gap-2.5">
          <AlertTriangle className="h-4 w-4 text-rose-600 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <span className="font-bold text-sm block">{opportunity?.title}</span>
            <p className="text-rose-700 dark:text-rose-300">
              Are you sure you want to permanently delete this opportunity for{" "}
              <strong>{opportunity?.client}</strong>? This action cannot be undone and deletes all
              associated draft progress.
            </p>
          </div>
        </div>

        <p className="text-xs text-slate-500 dark:text-slate-400">
          Tip: If you simply want to end the opportunity or remove it from the active pipeline,
          consider <strong>Archiving</strong> it instead so past records and metrics are preserved.
        </p>

        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg px-3.5 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            Cancel
          </button>

          <div className="flex items-center gap-2">
            {onArchiveInstead && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  if (opportunity) onArchiveInstead(opportunity);
                }}
                disabled={submitting}
                className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3.5 py-2 text-xs font-semibold text-amber-800 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
              >
                <Archive className="h-3.5 w-3.5 text-amber-600" />
                <span>Archive Instead</span>
              </button>
            )}

            <button
              type="button"
              onClick={handleDelete}
              disabled={submitting}
              className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-4 py-2 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
              <span>{submitting ? "Deleting..." : "Delete Permanently"}</span>
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
